import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Integration, Integration_Types } from 'app/models/integration.models';
import { IntegrationService } from 'app/services/integration.service';
import Swal from 'sweetalert2';

interface SortState {
  column: string;
  direction: 'asc' | 'desc';
}

@Component({
  selector: 'app-integrations-list',
  templateUrl: './integrations-list.component.html',
  styleUrls: ['./integrations-list.component.css']
})
export class IntegrationsListComponent implements OnInit {
  
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  // Data management
  dataSource = new MatTableDataSource<Integration>([]);
  loading = false;

  // Error management
  hasError = false;
  errorMessage = '';
  
  // Pagination
  totalItems = 0;
  pageSize = 20;
  currentPage = 0;
  
  // Search and filters
  searchTerm = '';
  selectedType = '';
  selectedStatus = '';
  selectedProvider = '';
  
  // Opciones de filtros disponibles
  integrationTypes = [
    { value: Integration_Types.PAYMENT_GATEWAY, label: 'Pasarela de Pago' },
    { value: Integration_Types.SHIPPING, label: 'Envíos y Logística' },
    { value: Integration_Types.INVENTORY, label: 'Gestión de Inventario' },
    { value: Integration_Types.ACCOUNTING, label: 'Contabilidad' },
    { value: Integration_Types.CRM, label: 'Gestión de Clientes (CRM)' },
    { value: Integration_Types.EMAIL, label: 'Email Marketing' },
    { value: Integration_Types.SMS, label: 'SMS y Mensajería' },
    { value: Integration_Types.SOCIAL_MEDIA, label: 'Redes Sociales' },
    { value: Integration_Types.ANALYTICS, label: 'Analíticas y Métricas' },
    { value: Integration_Types.WEBHOOK, label: 'Webhook Personalizado' }
  ];
  
  providers: string[] = [];
  
  // Sorting
  currentSort: SortState = { column: 'name', direction: 'asc' };

  constructor(
    private integrationService: IntegrationService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router
  ) {
    // Actualizar título del sidebar
    const sidebar = <HTMLElement>document.querySelector(".navbar-brand");
    if (sidebar !== undefined) {
      sidebar.innerHTML = "Gestión de Integraciones";
    }
  }

  ngOnInit(): void {
    this.loadIntegrations();
    this.loadProviders();
  }

  /**
   * Carga las integraciones con filtros aplicados
   */
  loadIntegrations(): void {
    this.loading = true;
    this.hasError = false;
    this.errorMessage = '';

    // Crear parámetros de búsqueda
    const params = {
      page: this.currentPage + 1, // API usa base 1
      limit: this.pageSize,
      search: this.searchTerm || undefined,
      type: this.selectedType || undefined,
      provider: this.selectedProvider || undefined,
      isActive: this.selectedStatus ? this.selectedStatus === 'active' : undefined,
      sortBy: this.currentSort.column,
      sortDirection: this.currentSort.direction
    };

    this.integrationService.getIntegrations().subscribe(
      (integrations) => {
        this.dataSource.data = integrations;
        this.totalItems = integrations.length;
        this.loading = false;
        this.hasError = false;
        
        // Actualizar lista de proveedores
        this.providers = [...new Set(integrations
          .filter(integration => integration.provider)
          .map(integration => integration.provider!)
        )];
      },
      (error) => {
        console.error('Error loading integrations:', error);
        this.loading = false;
        this.hasError = true;
        
        // Manejo de diferentes tipos de error en español
        if (error.status === 404) {
          this.errorMessage = 'El servicio de integraciones no está disponible en este momento.';
        } else if (error.status === 500) {
          this.errorMessage = 'Error interno del servidor. Por favor, contacta al administrador del sistema.';
        } else if (error.status === 0) {
          this.errorMessage = 'No se puede conectar con el servidor. Verifica tu conexión a internet.';
        } else {
          this.errorMessage = error.message || 'Error desconocido al cargar las integraciones. Inténtalo nuevamente.';
        }
        
        this.showMessage(this.errorMessage, 'error');
      }
    );
  }

  /**
   * Carga la lista de proveedores disponibles (método legacy - no usado)
   */
  loadProviders(): void {
    this.integrationService.getIntegrations().subscribe(
      (integrations) => {
        const uniqueProviders = [...new Set(integrations
          .filter(i => i.provider)
          .map(i => i.provider!)
        )];
        this.providers = uniqueProviders.sort();
      },
      (error) => {
        console.error('Error loading providers:', error);
      }
    );
  }

  /**
   * Aplica el filtro de búsqueda
   */
  applyFilter(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
    this.currentPage = 0; // Reset to first page
    this.loadIntegrations();
  }

  /**
   * Aplica los filtros de tipo, estado y proveedor
   */
  applyFilters(): void {
    this.currentPage = 0; // Reset to first page
    this.loadIntegrations();
  }

  /**
   * Limpia todos los filtros
   */
  clearFilters(): void {
    this.searchTerm = '';
    this.selectedType = '';
    this.selectedStatus = '';
    this.selectedProvider = '';
    this.currentPage = 0;
    this.loadIntegrations();
  }

  /**
   * Verifica si hay filtros activos
   */
  hasFilters(): boolean {
    return !!(this.searchTerm || this.selectedType || this.selectedStatus || this.selectedProvider);
  }

  /**
   * Ordena por columna
   */
  sortByColumn(column: string): void {
    if (this.currentSort.column === column) {
      this.currentSort.direction = this.currentSort.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSort.column = column;
      this.currentSort.direction = 'asc';
    }
    
    // Implementar ordenamiento local o recargar con parámetros de orden
    this.sortData();
  }

  /**
   * Ordena los datos localmente
   */
  sortData(): void {
    const data = [...this.dataSource.data];
    const direction = this.currentSort.direction === 'asc' ? 1 : -1;
    
    data.sort((a, b) => {
      let valueA: any;
      let valueB: any;
      
      switch (this.currentSort.column) {
        case 'name':
          valueA = a.name?.toLowerCase() || '';
          valueB = b.name?.toLowerCase() || '';
          break;
        case 'type':
          valueA = a.type || '';
          valueB = b.type || '';
          break;
        case 'updatedAt':
          valueA = new Date(a.updatedAt || 0);
          valueB = new Date(b.updatedAt || 0);
          break;
        default:
          return 0;
      }
      
      if (valueA < valueB) return -1 * direction;
      if (valueA > valueB) return 1 * direction;
      return 0;
    });
    
    this.dataSource.data = data;
  }

  /**
   * Maneja el cambio de página
   */
  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadIntegrations();
  }

  /**
   * Navega a la edición de una integración
   */
  navigateToEdit(id: number, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/configurar/integraciones', id]);
  }

  /**
   * Abre el diálogo de creación
   */
  openCreateDialog(): void {
    this.router.navigate(['/configurar/integraciones/create']);
  }

  /**
   * Abre el diálogo de edición
   */
  openEditDialog(integration: Integration, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/configurar/integraciones', integration.id]);
  }

  /**
   * Edita una integración (alias para openEditDialog)
   */
  editIntegration(integration: Integration, event: Event): void {
    this.openEditDialog(integration, event);
  }

  /**
   * Crea una nueva integración (alias para openCreateDialog)
   */
  createIntegration(): void {
    this.openCreateDialog();
  }

  /**
   * Alterna el estado activo/inactivo de una integración
   */
  toggleStatus(integration: Integration, event: Event): void {
    event.stopPropagation();
    const target = event.target as HTMLInputElement;
    const newStatus = target.checked;
    
    if (!integration.id) {
      this.showMessage('Error: ID de integración no válido', 'error');
      target.checked = !newStatus;
      return;
    }

    this.integrationService.toggleIntegrationStatus(integration.id, newStatus).subscribe(
      (updatedIntegration) => {
        integration.isActive = updatedIntegration.isActive;
        const statusText = newStatus ? 'activada' : 'desactivada';
        this.showMessage(`Integración ${statusText} exitosamente`, 'success');
      },
      (error) => {
        console.error('Error toggling integration status:', error);
        target.checked = !newStatus; // Revert toggle
        this.showMessage('Error al cambiar el estado de la integración', 'error');
      }
    );
  }





  /**
   * Elimina una integración
   */
  deleteIntegration(integration: Integration, event: Event): void {
    event.stopPropagation();
    
    if (!integration.id) {
      this.showMessage('Error: ID de integración no válido', 'error');
      return;
    }

    if (integration.isSystem) {
      this.showMessage('No se pueden eliminar integraciones del sistema', 'warning');
      return;
    }

    // Usar SweetAlert2 para confirmar eliminación
    Swal.fire({
      title: '¿Eliminar integración?',
      html: `
        <div class="text-start">
          <div class="mb-3">
            <strong class="integration-name">${integration.name}</strong>
          </div>
          <div class="warning-box">
            <i class="material-icons warning-icon">warning_amber</i>
            <div class="warning-content">
              <div class="warning-title">Esta acción no se puede deshacer</div>
              <div class="warning-subtitle">Se eliminará toda la configuración de esta integración</div>
            </div>
          </div>
        </div>
      `,
      icon: 'warning',
      iconColor: '#ff6b35',
      showCancelButton: true,
      confirmButtonColor: '#ff6b35',
      cancelButtonColor: '#8e9aaf',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
      focusCancel: true,
      background: '#ffffff',
      color: '#2c3e50',
      customClass: {
        popup: 'swal-modern-popup',
        title: 'swal-modern-title',
        htmlContainer: 'swal-modern-html',
        confirmButton: 'swal-modern-confirm',
        cancelButton: 'swal-modern-cancel',
        icon: 'swal-modern-icon'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.integrationService.deleteIntegration(integration.id).subscribe(
          () => {
            Swal.fire({
              title: '¡Eliminada!',
              text: `La integración "${integration.name}" ha sido eliminada exitosamente.`,
              icon: 'success',
              iconColor: '#27ae60',
              confirmButtonColor: '#27ae60',
              confirmButtonText: 'Perfecto',
              background: '#ffffff',
              color: '#2c3e50',
              customClass: {
                popup: 'swal-modern-popup',
                confirmButton: 'swal-modern-success'
              }
            });
            this.loadIntegrations(); // Refresh list
          },
          (error) => {
            console.error('Error deleting integration:', error);
            let errorMsg = 'Error al eliminar la integración';
            let errorDetail = 'Ha ocurrido un error inesperado.';
            
            if (error.status === 409) {
              errorMsg = 'No se puede eliminar';
              errorDetail = 'La integración está siendo utilizada por otros componentes del sistema.';
            } else if (error.status === 404) {
              errorMsg = 'Integración no encontrada';
              errorDetail = 'La integración ya no existe en el sistema.';
            }
            
            Swal.fire({
              title: errorMsg,
              text: errorDetail,
              icon: 'error',
              iconColor: '#e74c3c',
              confirmButtonColor: '#e74c3c',
              confirmButtonText: 'Entendido',
              background: '#ffffff',
              color: '#2c3e50',
              customClass: {
                popup: 'swal-modern-popup',
                confirmButton: 'swal-modern-error'
              }
            });
          }
        );
      }
    });
  }

  /**
   * Exporta integraciones
   */
  exportIntegrations(): void {
    this.integrationService.exportIntegrations().subscribe(
      (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `integraciones_${new Date().getTime()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.showMessage('Integraciones exportadas exitosamente', 'success');
      },
      (error) => {
        console.error('Error exporting integrations:', error);
        this.showMessage('Error al exportar las integraciones', 'error');
      }
    );
  }

  /**
   * Importa integraciones
   */
  importIntegrations(): void {
    this.fileInput.nativeElement.click();
  }

  /**
   * Maneja la selección de archivo para importación
   */
  onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    
    if (file) {
      this.integrationService.importIntegrations(file).subscribe(
        (result) => {
          if (result.success) {
            this.showMessage(`${result.imported} integraciones importadas exitosamente`, 'success');
            this.loadIntegrations(); // Refresh list
          } else {
            const errorMsg = result.errors?.join(', ') || 'Error desconocido';
            this.showMessage(`Error en importación: ${errorMsg}`, 'error');
          }
        },
        (error) => {
          console.error('Error importing integrations:', error);
          this.showMessage('Error al importar las integraciones', 'error');
        }
      );
      
      // Reset file input
      target.value = '';
    }
  }

  /**
   * Muestra estadísticas
   */
  viewStats(): void {
    this.integrationService.getIntegrationStats().subscribe(
      (stats) => {
        // Aquí podrías abrir un modal con las estadísticas
        console.log('Integration stats:', stats);
        this.showMessage('Ver estadísticas en consola (implementar modal)', 'info');
      },
      (error) => {
        console.error('Error loading stats:', error);
        this.showMessage('Error al cargar las estadísticas', 'error');
      }
    );
  }



  /**
   * Obtiene la etiqueta para un tipo de integración
   */
  getTypeLabel(type: Integration_Types): string {
    const typeOption = this.integrationTypes.find(t => t.value === type);
    return typeOption?.label || type;
  }

  /**
   * Función de tracking para ngFor
   */
  trackByIntegration(index: number, integration: Integration): any {
    return integration.id || index;
  }

  /**
   * Muestra un mensaje al usuario
   */
  private showMessage(message: string, type: 'success' | 'error' | 'warning' | 'info'): void {
    const config: any = { duration: 4000 };
    
    switch (type) {
      case 'success':
        config.panelClass = ['success-snackbar'];
        break;
      case 'error':
        config.panelClass = ['error-snackbar'];
        break;
      case 'warning':
        config.panelClass = ['warning-snackbar'];
        break;
      case 'info':
        config.panelClass = ['info-snackbar'];
        break;
    }
    
    this.snackBar.open(message, 'Cerrar', config);
  }

  /**
   * Obtiene la clase CSS para el badge del tipo
   */
  getTypeBadgeClass(type: string): string {
    switch (type) {
      case Integration_Types.PAYMENT_GATEWAY:
        return 'badge-payment';
      case Integration_Types.SHIPPING:
        return 'badge-shipping';
      case Integration_Types.INVENTORY:
        return 'badge-inventory';
      case Integration_Types.ACCOUNTING:
        return 'badge-accounting';
      case Integration_Types.CRM:
        return 'badge-crm';
      case Integration_Types.EMAIL:
        return 'badge-email';
      case Integration_Types.SMS:
        return 'badge-sms';
      case Integration_Types.SOCIAL_MEDIA:
        return 'badge-social';
      case Integration_Types.ANALYTICS:
        return 'badge-analytics';
      case Integration_Types.WEBHOOK:
        return 'badge-webhook';
      default:
        return 'badge-default';
    }
  }

  /**
   * Obtiene el ícono Material para cada tipo de integración
   */
  getTypeIcon(type: string): string {
    switch (type) {
      case Integration_Types.PAYMENT_GATEWAY:
        return 'payment';
      case Integration_Types.SHIPPING:
        return 'local_shipping';
      case Integration_Types.INVENTORY:
        return 'inventory_2';
      case Integration_Types.ACCOUNTING:
        return 'account_balance';
      case Integration_Types.CRM:
        return 'groups';
      case Integration_Types.EMAIL:
        return 'email';
      case Integration_Types.SMS:
        return 'sms';
      case Integration_Types.SOCIAL_MEDIA:
        return 'share';
      case Integration_Types.ANALYTICS:
        return 'analytics';
      case Integration_Types.WEBHOOK:
        return 'webhook';
      default:
        return 'extension';
    }
  }

  /**
   * Obtiene el ícono para mostrar en la integración (método legacy para compatibilidad)
   */
  getIntegrationIcon(type: string): string {
    return this.getTypeIcon(type);
  }

  /**
   * Reintenta cargar las integraciones
   */
  retryLoad(): void {
    this.loadIntegrations();
  }
}